import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import type { AccountDialing, ServiceConnections } from "@voice/api-contract";
import { checkDialing, type DialingResult } from "@voice/domain";
import { AccountDialingSettings } from "./AccountDialingSettings";
import { AssignmentEditor } from "./AssignmentEditor";
import {
  clearConnectionObservations,
  readConnectionSnapshot,
  equipmentIdentity,
  LineRegistration,
  type ConnectionFocus,
} from "./AccountConnections";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
type Props = {
  api: Api;
  csrf?: string | undefined;
  accountId: string;
  customer: boolean;
  readOnly?: boolean;
  commercial: boolean;
  view: "services" | "routing" | "equipment";
  refreshVersion: number;
  onSaved: () => Promise<void>;
  onRouting: () => void;
  onEquipment: () => void;
  onNumbers: () => void;
  onExtensions: () => void;
  onLocations: () => void;
  focus?: ConnectionFocus;
};
type Line = ServiceConnections["lines"][number];
export const connectionLineLabel = (line: Line) =>
  line.digits ? `${line.digits} · ${line.label}` : line.label;
const deviceLabel = equipmentIdentity;
const pendingNote =
  "Saving changes records the requested setup. Check each line's installed connection and live registration below; saving does not change live calls.";

export function DialingCheck({
  extensions,
  primaryNumber = null,
}: {
  extensions: string[];
  primaryNumber?: string | null;
}) {
  const [digits, setDigits] = useState("");
  const [result, setResult] = useState<DialingResult | null>(null);
  const localPrimary =
    primaryNumber && checkDialing(primaryNumber).destination === primaryNumber
      ? primaryNumber
      : null;
  return (
    <section className="cr-panel cr-connection-section">
      <h3>Dialing rules</h3>
      <dl className="cr-details">
        <div>
          <dt>911</dt>
          <dd>Direct emergency dialing. No outside-line prefix.</dd>
        </div>
        <div>
          <dt>988</dt>
          <dd>Direct Suicide &amp; Crisis Lifeline dialing.</dd>
        </div>
        <div>
          <dt>Seven-digit local calls</dt>
          <dd>
            {localPrimary
              ? `Use area code ${localPrimary.slice(2, 5)} from this account's saved primary number. The seven dialed digits already include the exchange.`
              : "Choose an owned primary number above to establish this account's area code. Without one, enter the full ten-digit number."}
          </dd>
        </div>
        <div>
          <dt>Other North American calls</dt>
          <dd>
            Ten digits, including the area code. Also accepts 1 or +1 before the
            full number. A supplied area code is never replaced.
          </dd>
        </div>
        <div>
          <dt>Extensions</dt>
          <dd>
            Must not conflict with 911, 988 or other reserved short codes.
          </dd>
        </div>
      </dl>
      <p className="cr-form-note">
        This is a format preview, not a reading from your phone. Check the
        primary setting's applied status above; device settings, calling
        permissions and emergency location are separate. This check never places
        a call.
      </p>
      <form
        className="cr-form"
        onSubmit={(event) => {
          event.preventDefault();
          setResult(checkDialing(digits, extensions, localPrimary));
        }}
      >
        <label className="cr-field">
          <span>Check a dialed number</span>
          <input
            value={digits}
            onChange={(event) => {
              setDigits(event.target.value);
              setResult(null);
            }}
            required
            maxLength={40}
            inputMode="tel"
            autoComplete="off"
          />
        </label>
        <button className="cr-button secondary">
          Check rules without calling
        </button>
      </form>
      {result && (
        <div
          className={`cr-notice ${result.kind === "invalid" ? "error" : ""}`}
          role="status"
        >
          <strong>
            {result.destination
              ? `Destination: ${result.destination}`
              : "Not accepted by these rules"}
          </strong>
          <p>{result.explanation}</p>
        </div>
      )}
    </section>
  );
}

function LineEditor({
  data,
  line,
  busy,
  onSave,
}: {
  data: ServiceConnections;
  line: Line;
  busy: boolean;
  onSave: (path: string, body: Record<string, unknown>) => Promise<void>;
}) {
  const [deviceId, setDeviceId] = useState(line.deviceId ?? "");
  const [slot, setSlot] = useState(line.deviceSlot ?? 1);
  const device = data.devices.find((item) => item.id === deviceId);
  const options = data.devices.filter(
    (item) =>
      item.locationId === line.locationId &&
      !["retired", "quarantined"].includes(item.status),
  );
  const usableNumbers = data.numbers.filter((item) => item.eligible);
  const occupied = data.lines
    .filter((item) => item.id !== line.id && item.deviceId === deviceId)
    .map((item) => item.deviceSlot);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    await onSave("equipment", {
      lineId: line.id,
      expectedVersion: line.version,
      expectedLegacyVersion: line.legacyVersion ?? "0",
      deviceId: deviceId || null,
      deviceSlot: deviceId ? slot : null,
      outgoingNumberId: String(form.get("outgoing") ?? "") || null,
      emergencyNumberId: String(form.get("emergency") ?? "") || null,
    });
  };
  return (
    <form
      className="cr-form"
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <fieldset className="cr-connection-fields" disabled={busy}>
        <legend>
          {connectionLineLabel(line)} · {line.locationName}
        </legend>
        <label className="cr-field">
          <span>Equipment</span>
          <select
            value={deviceId}
            onChange={(event) => {
              setDeviceId(event.target.value);
              const next = data.devices.find(
                (item) => item.id === event.target.value,
              );
              const used = new Set(
                data.lines
                  .filter(
                    (item) =>
                      item.id !== line.id &&
                      item.deviceId === event.target.value,
                  )
                  .map((item) => item.deviceSlot),
              );
              setSlot(
                Array.from(
                  { length: next?.slots ?? 1 },
                  (_, index) => index + 1,
                ).find((value) => !used.has(value)) ?? 1,
              );
            }}
          >
            <option value="">Not assigned</option>
            {deviceId && !options.some((item) => item.id === deviceId) && (
              <option value={deviceId}>Current equipment needs review</option>
            )}
            {options.map((item) => (
              <option value={item.id} key={item.id}>
                {deviceLabel(item)}
              </option>
            ))}
          </select>
        </label>
        {deviceId && (
          <label className="cr-field">
            <span>ATA port / SIP account</span>
            <select
              value={slot}
              onChange={(event) => setSlot(Number(event.target.value))}
            >
              {Array.from(
                { length: Math.max(device?.slots ?? 1, slot) },
                (_, i) => i + 1,
              ).map((value) => (
                <option
                  key={value}
                  value={value}
                  disabled={occupied.includes(value)}
                >
                  {value}
                  {occupied.includes(value)
                    ? " · Assigned to another line"
                    : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="cr-field">
          <span>Outgoing caller ID</span>
          <select name="outgoing" defaultValue={line.outgoingNumberId ?? ""}>
            <option value="">Choose later</option>
            {line.outgoingNumberId &&
              !usableNumbers.some(
                (item) => item.id === line.outgoingNumberId,
              ) && (
                <option value={line.outgoingNumberId}>
                  Previously selected number needs review
                </option>
              )}
            {usableNumbers.map((item) => (
              <option value={item.id} key={item.id}>
                {item.number}
              </option>
            ))}
          </select>
        </label>
        <label className="cr-field">
          <span>Emergency callback number</span>
          <select name="emergency" defaultValue={line.emergencyNumberId ?? ""}>
            <option value="">Choose later</option>
            {line.emergencyNumberId &&
              !usableNumbers.some(
                (item) => item.id === line.emergencyNumberId,
              ) && (
                <option value={line.emergencyNumberId}>
                  Previously selected number needs review
                </option>
              )}
            {usableNumbers.map((item) => (
              <option value={item.id} key={item.id}>
                {item.number}
              </option>
            ))}
          </select>
        </label>
      </fieldset>
      <p className="cr-form-note">
        Only equipment at this line's physical location is listed. HT802 models
        expose two ports; other models currently use the first account only.
        Assigning a port does not provision its SIP credentials.
      </p>
      <p className="cr-form-note">
        The emergency callback choice must be verified against the physical
        location and the actual emergency route. Saving it does not validate
        E911 or unlock connection details.
      </p>
      <button
        className="cr-button"
        disabled={busy || line.status === "disconnected"}
      >
        {busy ? "Saving…" : "Save connection"}
      </button>
    </form>
  );
}

export function ConnectionsContent({
  data,
  props,
  busy,
  save,
}: {
  data: ServiceConnections;
  props: Props;
  busy: boolean;
  save: (path: string, body: Record<string, unknown>) => Promise<void>;
}) {
  const editable = !props.customer && !props.readOnly;
  const [lineId, setLineId] = useState("");
  const [numberId, setNumberId] = useState("");
  const dialingScope = `${props.customer}:${props.accountId}`;
  const [dialingSnapshot, setDialingSnapshot] = useState<{
    scope: string;
    data: AccountDialing;
  } | null>(null);
  const dialing =
    dialingSnapshot?.scope === dialingScope
      ? dialingSnapshot.data
      : (data.dialing ?? null);
  const activeLines = data.lines.filter(
    (line) => line.status !== "disconnected",
  );
  const selectedLine =
    activeLines.find((line) => line.id === lineId) ??
    (activeLines.length === 1 ? activeLines[0] : undefined);
  const eligibleNumbers = data.numbers.filter((number) => number.eligible);
  const selectedNumber =
    eligibleNumbers.find((number) => number.id === numberId) ??
    (eligibleNumbers.length === 1 ? eligibleNumbers[0] : undefined);
  const numberText = (id: string | null) =>
    data.numbers.find((number) => number.id === id)?.number ?? "Not chosen";
  const unconnectedNumbers = data.numbers.filter(
    (number) =>
      number.eligible &&
      !data.lines.some(
        (line) => line.id === number.lineId && line.status !== "disconnected",
      ),
  );
  const unconnectedDevices = data.devices.filter(
    (device) =>
      !["retired", "quarantined"].includes(device.status) &&
      !data.lines.some(
        (line) => line.deviceId === device.id && line.status !== "disconnected",
      ),
  );
  return (
    <div className="cr-connections">
      {props.view === "routing" && (
        <AccountDialingSettings
          api={props.api}
          csrf={props.csrf}
          accountId={props.accountId}
          customer={props.customer}
          readOnly={!!props.readOnly}
          numbers={data.numbers}
          initialDialing={data.dialing}
          refreshVersion={props.refreshVersion}
          onChanged={(current) =>
            setDialingSnapshot({ scope: dialingScope, data: current })
          }
        />
      )}
      {editable && props.view !== "services" && (
        <AssignmentEditor
          key={JSON.stringify([
            props.focus,
            data.lines.map((line) => [
              line.id,
              line.version,
              line.legacyVersion,
            ]),
            data.numbers.map((number) => [number.id, number.version]),
            data.devices.map((device) => [device.id, device.version]),
          ])}
          data={data}
          commercial={props.commercial}
          busy={busy}
          focus={props.focus}
          save={save}
          onLocations={props.onLocations}
          onExtensions={props.onExtensions}
        />
      )}
      <section className="cr-panel cr-connection-section">
        <h3>
          {props.view === "services"
            ? "Telephone lines"
            : props.view === "equipment"
              ? "Numbers and extensions on equipment"
              : "Saved call connections"}
        </h3>
        <p className="cr-form-note">
          {props.customer
            ? "Your number, extension and phone assignments. Registration is shown separately from call testing."
            : pendingNote}
        </p>
        {data.lines.length ? (
          <div className="cr-connection-list">
            {data.lines.map((line) => {
              const device = data.devices.find(
                (item) => item.id === line.deviceId,
              );
              const incoming = data.numbers.filter(
                (item) => item.lineId === line.id,
              );
              return (
                <article key={line.id}>
                  <h4>{connectionLineLabel(line)}</h4>
                  <p className="cr-form-note">
                    {line.locationName}
                    {line.status === "disconnected"
                      ? " · Disconnected record"
                      : ""}
                  </p>
                  <LineRegistration line={line} />
                  <dl className="cr-details">
                    <div>
                      <dt>Incoming numbers</dt>
                      <dd>
                        {incoming.length
                          ? incoming.map((item) => item.number).join(", ")
                          : "None assigned"}
                      </dd>
                    </div>
                    <div>
                      <dt>Equipment</dt>
                      <dd>
                        {device
                          ? `${deviceLabel(device)} · Port/account ${line.deviceSlot ?? "unconfirmed"}`
                          : "Not assigned"}
                      </dd>
                    </div>
                    <div>
                      <dt>Outgoing caller ID</dt>
                      <dd>{numberText(line.outgoingNumberId)}</dd>
                    </div>
                    <div>
                      <dt>Emergency callback</dt>
                      <dd>
                        {numberText(line.emergencyNumberId)} · Not verified
                      </dd>
                    </div>
                  </dl>
                  {line.issues.map((issue) => (
                    <p className="cr-notice error" key={issue}>
                      {issue}
                    </p>
                  ))}
                  {editable &&
                    (line.deviceId ||
                      line.outgoingNumberId ||
                      line.emergencyNumberId) && (
                      <details className="cr-editor">
                        <summary>Clear saved equipment and caller IDs</summary>
                        <p>
                          This clears the portal's choices for this line. It
                          does not disconnect a live phone or remove
                          incoming-number destinations.
                        </p>
                        <button
                          className="cr-button secondary"
                          disabled={busy}
                          onClick={() => {
                            void save("equipment", {
                              lineId: line.id,
                              expectedVersion: line.version,
                              expectedLegacyVersion: line.legacyVersion ?? "0",
                              deviceId: null,
                              deviceSlot: null,
                              outgoingNumberId: null,
                              emergencyNumberId: null,
                            });
                          }}
                        >
                          Clear saved connection
                        </button>
                      </details>
                    )}
                </article>
              );
            })}
          </div>
        ) : (
          <p className="cr-empty">
            No telephone lines yet.{" "}
            {!editable
              ? "Cache River needs to finish line setup. Any numbers and equipment already on your account are listed below."
              : props.commercial
                ? "Add an extension to create its line."
                : "Add a line at the service location below."}
          </p>
        )}
        {(unconnectedNumbers.length > 0 || unconnectedDevices.length > 0) && (
          <section
            className="cr-account-resources"
            aria-label="Account resources awaiting connection"
          >
            <h4>On this account, awaiting connection</h4>
            <ul className="cr-account-resource-list">
              {unconnectedNumbers.map((number) => (
                <li key={number.id}>
                  <div>
                    <strong>{number.number}</strong>
                    <p>
                      Telephone number · {number.status.replaceAll("_", " ")} ·
                      No saved line destination
                    </p>
                  </div>
                  <button
                    className="cr-button secondary"
                    onClick={props.onNumbers}
                  >
                    View number
                  </button>
                </li>
              ))}
              {unconnectedDevices.map((device) => (
                <li key={device.id}>
                  <div>
                    <strong>{deviceLabel(device)}</strong>
                    <p>
                      Equipment ·{" "}
                      {data.locations.find(
                        (location) => location.id === device.locationId,
                      )?.name ?? "Location not assigned"}{" "}
                      · No saved line connection
                    </p>
                  </div>
                  <button
                    className="cr-button secondary"
                    onClick={props.onEquipment}
                  >
                    View equipment
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <div className="cr-actions">
          {props.view === "services" && (
            <button className="cr-button secondary" onClick={props.onRouting}>
              {editable
                ? "Assign numbers and equipment"
                : "View call connections"}
            </button>
          )}
          <button className="cr-button secondary" onClick={props.onNumbers}>
            Open Numbers
          </button>
          <button className="cr-button secondary" onClick={props.onEquipment}>
            Open Equipment
          </button>
          {props.commercial && (
            <button
              className="cr-button secondary"
              onClick={props.onExtensions}
            >
              Open Extensions
            </button>
          )}
        </div>
      </section>
      {props.view === "services" && editable && !props.commercial && (
        <section className="cr-panel cr-connection-section">
          <h3>Add a telephone line</h3>
          {data.locations.length ? (
            <form
              className="cr-form"
              onSubmit={(event) => {
                event.preventDefault();
                const form = new FormData(event.currentTarget);
                void save("lines", {
                  label: String(form.get("label") ?? ""),
                  locationId: String(form.get("location") ?? ""),
                });
              }}
            >
              <fieldset className="cr-connection-fields" disabled={busy}>
                <legend>Residential line</legend>
                <label className="cr-field">
                  <span>Line name</span>
                  <input name="label" required maxLength={120} />
                </label>
                <label className="cr-field">
                  <span>Physical service location</span>
                  <select
                    name="location"
                    required
                    defaultValue={
                      data.locations.length === 1 ? data.locations[0]!.id : ""
                    }
                  >
                    <option value="">Choose location</option>
                    {data.locations.map((location) => (
                      <option value={location.id} key={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </label>
              </fieldset>
              <button className="cr-button" disabled={busy}>
                {busy ? "Saving…" : "Add line"}
              </button>
              <p className="cr-form-note">
                Adds a line for setup. It does not activate service, purchase a
                number or change billing.
              </p>
            </form>
          ) : (
            <button className="cr-button secondary" onClick={props.onLocations}>
              Add a service location
            </button>
          )}
        </section>
      )}
      {(props.view === "routing" || props.view === "equipment") && (
        <>
          {editable && (
            <details className="cr-panel cr-editor">
              <summary>Advanced: edit individual connection records</summary>
              <p>
                Use the complete assignment above for normal setup. These
                separate controls also allow records to be cleared or repaired
                individually.
              </p>
              {editable && (
                <section className="cr-panel cr-connection-section">
                  <h3>Connect equipment to a line</h3>
                  {activeLines.length ? (
                    <>
                      <label className="cr-field">
                        <span>Line or extension</span>
                        <select
                          value={selectedLine?.id ?? ""}
                          onChange={(event) => setLineId(event.target.value)}
                          disabled={busy}
                        >
                          <option value="">Choose line</option>
                          {activeLines.map((line) => (
                            <option value={line.id} key={line.id}>
                              {connectionLineLabel(line)}
                            </option>
                          ))}
                        </select>
                      </label>
                      {selectedLine && (
                        <LineEditor
                          key={`${selectedLine.id}:${selectedLine.version}`}
                          data={data}
                          line={selectedLine}
                          busy={busy}
                          onSave={save}
                        />
                      )}
                    </>
                  ) : (
                    <p className="cr-empty">
                      Add a line in Services
                      {props.commercial
                        ? " or an extension in Extensions"
                        : ""}{" "}
                      first.
                    </p>
                  )}
                </section>
              )}
              {editable && (
                <section className="cr-panel cr-connection-section">
                  <h3>Choose what rings for a number</h3>
                  {eligibleNumbers.length && activeLines.length ? (
                    <>
                      <label className="cr-field">
                        <span>Owned number on this account</span>
                        <select
                          value={selectedNumber?.id ?? ""}
                          onChange={(event) => setNumberId(event.target.value)}
                          disabled={busy}
                        >
                          <option value="">Choose number</option>
                          {eligibleNumbers.map((number) => (
                            <option value={number.id} key={number.id}>
                              {number.number}
                            </option>
                          ))}
                        </select>
                      </label>
                      {selectedNumber && (
                        <form
                          key={`${selectedNumber.id}:${selectedNumber.version}`}
                          className="cr-form"
                          onSubmit={(event) => {
                            event.preventDefault();
                            const form = new FormData(event.currentTarget);
                            void save("numbers", {
                              numberId: selectedNumber.id,
                              expectedVersion: selectedNumber.version,
                              lineId: String(form.get("line") ?? "") || null,
                            });
                          }}
                        >
                          <label className="cr-field">
                            <span>Ring this line or extension</span>
                            <select
                              name="line"
                              defaultValue={selectedNumber.lineId ?? ""}
                              disabled={busy}
                            >
                              <option value="">No destination</option>
                              {selectedNumber.lineId &&
                                !activeLines.some(
                                  (line) => line.id === selectedNumber.lineId,
                                ) && (
                                  <option value={selectedNumber.lineId}>
                                    Previous line needs review
                                  </option>
                                )}
                              {activeLines.map((line) => (
                                <option value={line.id} key={line.id}>
                                  {connectionLineLabel(line)}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button className="cr-button" disabled={busy}>
                            {busy ? "Saving…" : "Save incoming destination"}
                          </button>
                        </form>
                      )}
                    </>
                  ) : (
                    <p className="cr-empty">
                      Link an owned number in Numbers and add a telephone line
                      first. A pending port cannot be routed yet.
                    </p>
                  )}
                  <p className="cr-form-note">
                    Several numbers can ring the same line. This does not change
                    outgoing caller ID, emergency caller ID or carrier routing.
                  </p>
                </section>
              )}
            </details>
          )}
          {data.extensionConflicts.length > 0 && (
            <p className="cr-notice error" role="alert">
              Extensions requiring review: {data.extensionConflicts.join(", ")}.
              They conflict with protected service codes.
            </p>
          )}
          {editable &&
            data.numbers.some((number) => number.lineId !== null) && (
              <section className="cr-panel cr-connection-section">
                <h3>Clear an incoming destination</h3>
                <p>
                  Remove a saved destination without releasing the number or
                  changing live calls.
                </p>
                <div className="cr-actions">
                  {data.numbers
                    .filter((number) => number.lineId !== null)
                    .map((number) => (
                      <button
                        key={number.id}
                        className="cr-button secondary"
                        disabled={busy}
                        onClick={() => {
                          void save("numbers", {
                            numberId: number.id,
                            lineId: null,
                            expectedVersion: number.version,
                          });
                        }}
                      >
                        Clear destination for {number.number}
                      </button>
                    ))}
                </div>
              </section>
            )}
          {props.view === "routing" && (
            <>
              <DialingCheck
                key={`${dialingScope}:${dialing?.version ?? "none"}:${dialing?.primaryNumber ?? "none"}`}
                primaryNumber={dialing?.primaryNumber ?? null}
                extensions={data.lines.flatMap((line) =>
                  line.digits ? [line.digits] : [],
                )}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

export function ServiceConnectionsPanel(props: Props) {
  const [data, setData] = useState<ServiceConnections | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const alive = useRef(true);
  const inFlight = useRef(false);
  const pending = useRef<{ fingerprint: string; key: string } | null>(null);
  const { api, csrf, accountId, customer, refreshVersion, onSaved } = props;
  const base = `/${customer ? "customer" : "admin"}/accounts/${accountId}/connections`;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError("");
    let refreshing = false;
    const refresh = async (background = false) => {
      if (refreshing || controller.signal.aborted) return;
      refreshing = true;
      await readConnectionSnapshot(api, base, controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) {
            // A status poll must not advance optimistic write versions under an open editor.
            setData((previous) =>
              background && previous
                ? {
                    ...previous,
                    lines: previous.lines.map((line) => {
                      const fresh = result.lines.find(
                        (item) =>
                          item.id === line.id &&
                          item.version === line.version &&
                          item.deviceId === line.deviceId,
                      );
                      return {
                        ...line,
                        observation: fresh?.observation ?? {
                          registration: "unknown",
                          configuration: "unknown",
                          freshness: "unavailable",
                          checkedAt: null,
                        },
                      };
                    }),
                  }
                : result,
            );
            setError("");
          }
        })
        .catch((cause) => {
          if (!controller.signal.aborted) {
            setData((previous) =>
              background && previous
                ? clearConnectionObservations(previous)
                : null,
            );
            setError(
              background
                ? "Live connection status could not refresh. Your selections are preserved; registration is unavailable until the next successful check."
                : cause.message,
            );
          }
        });
      refreshing = false;
    };
    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible" && !inFlight.current)
        void refresh(true);
    }, 15000);
    const onVisible = () => {
      if (document.visibilityState === "visible" && !inFlight.current)
        void refresh(true);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, base, refreshVersion, revision]);
  const save = useCallback(
    async (path: string, body: Record<string, unknown>) => {
      if (inFlight.current || customer || props.readOnly) return;
      inFlight.current = true;
      setBusy(true);
      setError("");
      setNotice("");
      const fingerprint = JSON.stringify({ path, body });
      if (pending.current?.fingerprint !== fingerprint)
        pending.current = { fingerprint, key: crypto.randomUUID() };
      try {
        await api(`${base}/${path}`, {
          method: "POST",
          csrf,
          body: { ...body, requestKey: pending.current.key },
        });
        pending.current = null;
        if (alive.current) {
          setNotice("Saved. Not yet applied to the phone system.");
          setRevision((value) => value + 1);
        }
        await onSaved();
      } catch (cause) {
        if (alive.current) setError((cause as Error).message);
      } finally {
        inFlight.current = false;
        if (alive.current) setBusy(false);
      }
    },
    [api, base, csrf, customer, onSaved, props.readOnly],
  );
  return (
    <>
      {error && (
        <div className="cr-notice error" role="alert">
          {error}{" "}
          <button
            className="cr-button secondary"
            disabled={busy}
            onClick={() => setRevision((value) => value + 1)}
          >
            Refresh connections
          </button>
        </div>
      )}
      {notice && (
        <p className="cr-notice" role="status">
          {notice}
        </p>
      )}
      {!data && !error && <p role="status">Loading service connections…</p>}
      {data && (
        <ConnectionsContent data={data} props={props} busy={busy} save={save} />
      )}
    </>
  );
}
